import { useState, useEffect } from 'react'
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet'
import { Icon } from 'leaflet'
import { Building2, Users, Activity, MapPin, Search } from 'lucide-react'
import 'leaflet/dist/leaflet.css'

// Fix para iconos de Leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

// Datos demo de hospitales
const DEMO_HOSPITALES = [
  { id: 1, nombre: 'HGZ No. 53 Los Reyes', latitud: 19.3647, longitud: -98.9969, quirofanos: 8, camas: 245, ocupacion: 78, tipo: 'HGZ' },
  { id: 2, nombre: 'HGZ No. 57 La Quebrada', latitud: 19.4028, longitud: -99.0236, quirofanos: 6, camas: 198, ocupacion: 85, tipo: 'HGZ' },
  { id: 3, nombre: 'HGZ No. 68 Tulpetlac', latitud: 19.5847, longitud: -99.0089, quirofanos: 10, camas: 312, ocupacion: 62, tipo: 'HGZ' },
  { id: 4, nombre: 'HGZ No. 76 Xalostoc', latitud: 19.5234, longitud: -99.0567, quirofanos: 5, camas: 156, ocupacion: 91, tipo: 'HGZ' },
  { id: 5, nombre: 'HGR No. 196 Ecatepec', latitud: 19.4678, longitud: -99.0412, quirofanos: 12, camas: 380, ocupacion: 73, tipo: 'HGR' },
  { id: 6, nombre: 'HGZ No. 197 Texcoco', latitud: 19.5160, longitud: -98.8830, quirofanos: 4, camas: 120, ocupacion: 55, tipo: 'HGZ' },
]

// Componente para controlar el mapa
const MapController = ({ center, zoom }) => {
  const map = useMap()
  useEffect(() => {
    if (center) {
      map.flyTo(center, zoom, { duration: 0.8 })
    }
  }, [center, zoom, map])
  return null
}

export default function Mapa() {
  const [hospitales] = useState(DEMO_HOSPITALES)
  const [selectedHospital, setSelectedHospital] = useState(null)
  const [mapCenter, setMapCenter] = useState([19.45, -99.0])
  const [mapZoom, setMapZoom] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')

  // Filtrar hospitales
  const filteredHospitales = hospitales.filter(h =>
    h.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Click en hospital - zoom al mapa
  const handleHospitalClick = (hospital) => {
    setSelectedHospital(hospital)
    setMapCenter([hospital.latitud, hospital.longitud])
    setMapZoom(14)
  }

  // Reset zoom
  const handleResetZoom = () => {
    setSelectedHospital(null)
    setMapCenter([19.45, -99.0])
    setMapZoom(10)
  }

  const getOcupacionColor = (ocupacion) => {
    if (ocupacion >= 85) return '#ef4444' // red
    if (ocupacion >= 70) return '#f59e0b' // amber
    return '#10b981' // green
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Mapa de Hospitales</h1>
          <p className="text-gray-500">{hospitales.length} hospitales en la zona</p>
        </div>
        {selectedHospital && (
          <button
            onClick={handleResetZoom}
            className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Ver todos
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Building2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{hospitales.length}</p>
              <p className="text-sm text-gray-500">Hospitales</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Activity className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{hospitales.reduce((sum, h) => sum + h.quirofanos, 0)}</p>
              <p className="text-sm text-gray-500">Quirofanos</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{hospitales.reduce((sum, h) => sum + h.camas, 0)}</p>
              <p className="text-sm text-gray-500">Camas</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Activity className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{Math.round(hospitales.reduce((sum, h) => sum + h.ocupacion, 0) / hospitales.length)}%</p>
              <p className="text-sm text-gray-500">Ocupacion Prom.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Mapa */}
        <div className="lg:col-span-3 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold text-gray-800">Ubicacion de Hospitales</h3>
            </div>
            <div className="flex gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span>&lt;70%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span>70-85%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span>&gt;85%</span>
              </div>
            </div>
          </div>
          <div style={{ height: '500px' }}>
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              style={{ height: '100%', width: '100%' }}
            >
              <MapController center={mapCenter} zoom={mapZoom} />
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap'
              />
              {hospitales.map((hospital) => (
                <CircleMarker
                  key={hospital.id}
                  center={[hospital.latitud, hospital.longitud]}
                  radius={selectedHospital?.id === hospital.id ? 18 : 14}
                  fillColor={getOcupacionColor(hospital.ocupacion)}
                  fillOpacity={0.9}
                  color="white"
                  weight={selectedHospital?.id === hospital.id ? 4 : 2}
                  eventHandlers={{
                    click: () => handleHospitalClick(hospital)
                  }}
                >
                  <Popup>
                    <div className="p-1">
                      <h3 className="font-bold text-gray-800">{hospital.nombre}</h3>
                      <p className="text-xs text-gray-500 mb-2">{hospital.tipo}</p>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-gray-500">Quirofanos:</span> {hospital.quirofanos}</p>
                        <p><span className="text-gray-500">Camas:</span> {hospital.camas}</p>
                        <p>
                          <span className="text-gray-500">Ocupacion:</span>
                          <span className={`ml-1 font-semibold ${
                            hospital.ocupacion >= 85 ? 'text-red-600' :
                            hospital.ocupacion >= 70 ? 'text-amber-600' : 'text-green-600'
                          }`}>
                            {hospital.ocupacion}%
                          </span>
                        </p>
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </div>

        {/* Lista de hospitales */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" style={{ maxHeight: '600px' }}>
          <div className="p-4 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Buscar hospital..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex-1 overflow-auto">
            <div className="p-2 space-y-2">
              {filteredHospitales.map((hospital) => (
                <div
                  key={hospital.id}
                  onClick={() => handleHospitalClick(hospital)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedHospital?.id === hospital.id
                      ? 'border-emerald-500 bg-emerald-50 shadow-sm'
                      : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-4 h-4 rounded-full mt-0.5 flex-shrink-0 ${
                      hospital.ocupacion >= 85 ? 'bg-red-500' :
                      hospital.ocupacion >= 70 ? 'bg-amber-500' : 'bg-green-500'
                    }`}></div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 text-sm">{hospital.nombre}</p>
                      <p className="text-xs text-gray-500">{hospital.tipo}</p>
                      <div className="flex gap-3 mt-2 text-xs text-gray-500">
                        <span>{hospital.quirofanos} quirofanos</span>
                        <span>{hospital.camas} camas</span>
                      </div>
                      <div className="mt-2">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-500">Ocupacion</span>
                          <span className={`font-medium ${
                            hospital.ocupacion >= 85 ? 'text-red-600' :
                            hospital.ocupacion >= 70 ? 'text-amber-600' : 'text-green-600'
                          }`}>{hospital.ocupacion}%</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              hospital.ocupacion >= 85 ? 'bg-red-500' :
                              hospital.ocupacion >= 70 ? 'bg-amber-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${hospital.ocupacion}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Leyenda */}
          <div className="p-4 border-t border-gray-100 bg-gray-50">
            <p className="text-xs font-medium text-gray-500 mb-2">Nivel de Ocupacion</p>
            <div className="flex justify-between text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-gray-600">Normal</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                <span className="text-gray-600">Alta</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="text-gray-600">Critica</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
